import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { prepareTransactionRequest, sendTransactionSync } from '@wagmi/core'
import { Remote } from 'accounts'
import { Address } from 'ox'
import { useEffect, useMemo } from 'react'
import { tempo } from 'viem/chains'
import { Actions, Addresses } from 'viem/tempo'
import { useConnections } from 'wagmi'

import * as AuthorizedApps from '#/lib/authorized-apps.js'
import { remote, wagmiConfig } from '#/lib/config.js'
import { useWebAuthnConnector } from '#/lib/connectors.js'
import { useAnalytics } from '#/lib/telemetry/analytics.js'
import * as TransactionFrames from '../-frames/transaction/index.js'
import { usePrepareAlert } from '../-hooks/usePrepareAlert.js'
import { useRequireFunds } from '../-hooks/useRequireFunds.js'
import * as RemoteRpc from '../-lib/RemoteRpc.js'
import * as SameSite from '../-lib/SameSite.js'

export const Route = createFileRoute('/remote/rpc/wallet_revokeAccessKey')({
  component: Component,
  validateSearch: (search) =>
    RemoteRpc.validateSearch(search, { method: 'wallet_revokeAccessKey' }),
})

type Search = Remote.validateSearch.ReturnType<'wallet_revokeAccessKey'>

class TransactionDetailsChangedError extends Error {
  override name = 'TransactionDetailsChangedError'

  constructor() {
    super('Transaction details changed. Review and revoke again.')
  }
}

/** Whether the target key has no on-chain record and must be authorized before revocation. */
export function isUnpublishedAccessKey(
  metadata: Actions.accessKey.getMetadata.ReturnValue | undefined,
  accessKeyAddress: Address.Address,
) {
  return !!metadata && !metadata.isRevoked && !Address.isEqual(metadata.address, accessKeyAddress)
}

function Component() {
  const { capture } = useAnalytics()
  const queryClient = useQueryClient()
  const search = Route.useSearch() as Search
  const rpc = RemoteRpc.use()
  const consumer = RemoteRpc.useConsumer()
  const host = consumer.name ?? consumer.host
  const params = search._decoded.params[0]
  const connector = useWebAuthnConnector()
  const connection = useConnections().find((x) => x.connector.uid === connector.uid)
  const address = connection?.accounts[0]
  const chainId = connection?.chainId
  const sameSite = SameSite.useValidate(rpc.consumer.origin)
  const feePayer = sameSite || typeof params.feePayer === 'string' ? params.feePayer : false

  const resolvedChainId = chainId ?? tempo.id

  useEffect(() => {
    capture('access_key_revoke_started')
  }, [capture])

  // Single `revokeKey(keyId)` call to the AccountKeychain precompile.
  const calls = useMemo(
    () => [Actions.accessKey.revoke.call({ accessKey: params.accessKeyAddress })],
    [params.accessKeyAddress],
  )

  const keyAuthorization_candidate = useMemo(() => {
    if (!address) return undefined
    if (params.keyAuthorization) return params.keyAuthorization
    return remote.provider.store.accessKeys.list({
      accessKey: params.accessKeyAddress,
      account: address,
      chainId: resolvedChainId,
    })[0]?.keyAuthorization
  }, [address, params.accessKeyAddress, params.keyAuthorization, resolvedChainId])
  const metadata = useQuery({
    enabled: !!address,
    queryFn: () =>
      Actions.accessKey.getMetadata(
        wagmiConfig.getClient({ chainId: resolvedChainId as typeof tempo.id }),
        {
          account: address!,
          accessKey: params.accessKeyAddress,
        },
      ),
    queryKey: ['access-key-metadata', resolvedChainId, address, params.accessKeyAddress],
    retry: false,
    staleTime: 0,
  })
  const unpublished = isUnpublishedAccessKey(metadata.data, params.accessKeyAddress)
  const keyAuthorization = unpublished ? keyAuthorization_candidate : undefined
  const missingAuthorization = unpublished && !keyAuthorization

  // Keep the signer explicit: Wagmi's prepare hook retains only the connector UID in
  // its query key, so its query function would fall back to the ambient connection.
  const prepare = useQuery({
    enabled: !!address && metadata.isSuccess && !missingAuthorization,
    gcTime: 0,
    queryFn: () =>
      prepareTransactionRequest(wagmiConfig, {
        calls,
        to: calls[0]?.to,
        chainId: resolvedChainId as typeof tempo.id,
        connector,
        feePayer: feePayer as never,
        ...(feePayer ? { feeToken: Addresses.pathUsd } : {}),
        ...(keyAuthorization ? { keyAuthorization } : {}),
        type: 'tempo',
      }),
    queryKey: [
      'access-key-revocation',
      connector.uid,
      resolvedChainId,
      address,
      calls,
      feePayer,
      keyAuthorization,
    ],
    refetchOnWindowFocus: false,
    // Poll while waiting for funds to arrive (deposit/onramp) so the
    // requireFunds capability clears as soon as a deposit confirms.
    refetchInterval: (query) => (query.state.data?._capabilities?.requireFunds ? 2_000 : false),
    retry: false,
    staleTime: 0,
  })

  const capabilities = prepare.data?._capabilities
  const autoSwap = capabilities?.autoSwap

  const funds = useRequireFunds({
    address,
    chainId: resolvedChainId,
    prepareQuery: prepare,
  })

  const confirm = useMutation({
    mutationFn: async () => {
      // Refresh expiring nonce fields after review, immediately before signing.
      const { data: tx_refreshed, error: error_refreshed } = await prepare.refetch()
      if (error_refreshed) throw error_refreshed
      if (!tx_refreshed) throw new Error('Transaction not prepared.')

      const {
        autoSwap: autoSwap_refreshed,
        error: error_capabilities,
        fee: fee_refreshed,
        requireFunds: requireFunds_refreshed,
        sponsor: sponsor_refreshed,
      } = tx_refreshed._capabilities ?? {}
      if (error_capabilities) throw new Error(error_capabilities.message)
      const calls_refreshed = autoSwap_refreshed?.calls
      const fee_reviewed = capabilities?.fee
      const changed =
        requireFunds_refreshed !== undefined ||
        fee_reviewed?.amount !== fee_refreshed?.amount ||
        fee_reviewed?.decimals !== fee_refreshed?.decimals ||
        fee_reviewed?.symbol !== fee_refreshed?.symbol ||
        (capabilities?.sponsor !== undefined) !== (sponsor_refreshed !== undefined) ||
        autoSwap?.calls?.length !== calls_refreshed?.length ||
        autoSwap?.calls?.some((call, index) => {
          const refreshed = calls_refreshed?.[index]
          return (
            call.to !== refreshed?.to ||
            call.data !== refreshed?.data ||
            call.value !== refreshed?.value
          )
        })
      if (changed) throw new TransactionDetailsChangedError()

      // Prepend autoSwap calls so missing funds source from other balances atomically.
      const swapCalls = autoSwap_refreshed?.calls
      const data = swapCalls
        ? { ...tx_refreshed, calls: [...swapCalls, ...(tx_refreshed.calls ?? [])] }
        : tx_refreshed
      await sendTransactionSync(wagmiConfig, { ...data, connector } as never)
      return rpc.resolve(undefined)
    },
    onSuccess: () => {
      capture('access_key_revoke_success')
      if (address)
        queryClient.invalidateQueries({
          queryKey: AuthorizedApps.queryKey(address, { chainId: resolvedChainId }),
        })
    },
    onError: (error) => {
      if (error instanceof TransactionDetailsChangedError) return
      capture('access_key_revoke_failed', { error: error.message })
    },
  })

  const prepareAlert = usePrepareAlert({
    mutation: confirm,
    query: prepare,
  })
  const alert = (() => {
    if (metadata.error) return { message: metadata.error.message, type: 'error' as const }
    if (missingAuthorization)
      return {
        message: 'The original authorization for this pending access key is unavailable.',
        type: 'error' as const,
      }
    return prepareAlert
  })()

  if (funds.frame) return funds.frame

  return (
    <TransactionFrames.RevokeAccessKey
      accessKeyAddress={params.accessKeyAddress}
      accountAddress={params.address}
      actions={funds.actions}
      alert={alert}
      autoSwap={autoSwap}
      confirming={confirm.isPending}
      fee={capabilities?.fee}
      host={host}
      loading={prepare.isLoading || metadata.isLoading}
      onConfirm={() => confirm.mutate()}
      onReject={() => rpc.reject()}
      onRetry={() => {
        confirm.reset()
        void metadata.refetch()
        void prepare.refetch()
      }}
      sponsor={capabilities?.sponsor}
    />
  )
}
